import { ref, computed, onUnmounted } from 'vue';

export function parseAiAnswerXml(xmlContent) {
  const { $t } = useNuxtApp();
  const result = {
    message: '',
    references: [],
  };

  try {
    if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
      return result;
    }
    if (!xmlContent) {
      return result;
    }

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');

    // Check for parser errors
    const parseError = xmlDoc.getElementsByTagName('parsererror');
    if (parseError.length > 0) {
      console.error('XML parsing error:', parseError[0].textContent);
      return result;
    }

    const answerElement = xmlDoc.getElementsByTagName('answer')[0];
    if (!answerElement) {
      console.error('No <answer> element found in XML');
      return result;
    }

    // Check if it's a no-answer case
    const noAnswerElement = answerElement.getElementsByTagName('no-answer')[0];
    if (noAnswerElement) {
      result.message = noAnswerElement.textContent?.trim() || $t('search.aiAnswer.noAnswer');
      return result;
    }

    // Extract answer content (text nodes and elements except reference)
    let answerContent = '';
    for (const child of answerElement.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        answerContent += child.textContent;
      } else if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'reference') {
        answerContent += child.outerHTML;
      }
    }

    result.message = answerContent.trim();

    // Extract all references
    const referenceElements = answerElement.getElementsByTagName('reference');
    for (let i = 0; i < referenceElements.length; i++) {
      const reference = referenceElements[i];
      const titleElement = reference.getElementsByTagName('title')[0];
      const urlElement = reference.getElementsByTagName('url')[0];

      if (titleElement && urlElement) {
        const title = titleElement.textContent?.trim();
        const url = urlElement.textContent?.trim();

        if (title && url) {
          result.references.push({
            title: title,
            url: url,
          });
        }
      }
    }

    return result;
  } catch (error) {
    console.error('Error parsing AI answer XML:', error);
    return result;
  }
}

export function useAiAnswer() {
  const { $t } = useNuxtApp();
  const message = ref('');
  const references = ref([]);
  const status = ref(''); // 'in_progress', 'complete', 'failed'
  const isLoading = ref(false);

  // Consolidated AI data object
  const aiData = computed(() => ({
    answer: message.value,
    references: references.value,
    status: status.value,
    isLoading: isLoading.value,
  }));

  let pollingInterval;
  const POLLING_INTERVAL = 1000; // Poll every 1 second

  const startPolling = async (chatId, sessionId) => {
    if (!chatId || !sessionId) {
      console.error('Chat ID and Session ID are required for AI answer polling');
      return;
    }
    stopPolling();

    isLoading.value = true;
    status.value = 'in_progress';

    const { $axios } = useNuxtApp();

    const pollChat = async () => {
      try {
        const response = await $axios.get(`api/v6/ai/chat/${chatId}`, {
          params: {
            session_id: sessionId,
          },
        });

        const chatData = response.data;
        status.value = chatData.status;

        if (chatData.status === 'complete') {
          const parsed = parseAiAnswerXml(chatData.last_message?.content);
          message.value = parsed.message;
          references.value = parsed.references;

          stopPolling();
        } else if (chatData.status === 'failed') {
          console.error('AI answer failed:', chatData.error || 'Unknown error');
          message.value = $t('search.aiAnswer.failed');

          stopPolling();
        } else if (chatData.status === 'in_progress') {
          pollingInterval = setTimeout(pollChat, POLLING_INTERVAL);
        }
      } catch (error) {
        console.error('Error polling AI answer:', error);
        message.value = $t('search.aiAnswer.failed');
        status.value = 'failed';

        stopPolling();
      }
    };

    await pollChat();
  };

  const stopPolling = () => {
    if (pollingInterval) {
      clearTimeout(pollingInterval);
      pollingInterval = null;
    }
    isLoading.value = false;
  };

  onUnmounted(() => {
    stopPolling();
  });

  return {
    aiData,
    startPolling,
    stopPolling,
  };
}
